import React, { FC, useState, useEffect } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ProductGallery } from '@components/molecules/ProductGallery';
import { RoundIcon, RoundIconVariants } from '@components/atoms/RoundIcon';
import { Tabs, TabsProps } from '@components/atoms/Tabs';
import { ProductCardProps } from '@components/molecules/ProductCard';
import { ProductsHighlights } from '@components/molecules/ProductsHighlights';
import { StickyHeader } from '@components/molecules/StickyHeader/StickyHeader';
import { BreadcrumbsBanner } from '@components/molecules/BreadcrumbsBanner';
import { Footer } from '@components/molecules/Footer';
import { MarketplaceAdvertising } from '@components/molecules/MarketplaceAdvertising';
import { Typography, TypographyAppearanceVariants, TypographyTagVariants } from '@components/atoms/Typography';
import { PopOver } from '@components/atoms/PopOver';
import { Space } from '@components/atoms/Space';
import { Container } from '@components/atoms/Container';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { ProductAttributesSelector } from '@components/molecules/ProductAttributesSelector';
import { useProductAvailability } from '@hooks/useProductAvailability';
import { Modules, useModule } from '@hooks/useModule';
import { MenuPages, ProductAvailability } from '@types';
import { formatPrice } from '@utils/format';
import styles from './ProductDetail.module.css';
import { SITE_URLS } from '@constants';
import { getVariantFromAttributes, displayOldPrice, displayPrice } from '@utils/products';
import { useThemes } from '@hooks/useThemes';
import { useAgeRestriction } from '@hooks/useAgeRestriction';
import { useData } from '@hooks/useData';
import { Rating } from '@components/molecules/Rating';
import { InputAmount } from '@components/atoms/InputAmount';
import { Button, ButtonMode } from '@components/atoms/Button';
import { useNotifications } from '@hooks/useNotifications';
import { useAddProductToCart } from '@services/useAddProductToCart';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { ReserveProductModal } from './_subcomponents/ReserveProductModal/ReserveProductModal';
import { ButtonGroup } from '@components/atoms/ButtonGroup';
import { getDynamicMenuItemValue } from '@utils/translation';

export interface ProductDetailProps {
    uuid?: string;
    name?: string;
    merchant?: {
        uuid?: string;
        name?: string;
    };
    category?: {
        uuid?: string;
        name?: string;
        over18?: number;
    };
    availability?: ProductAvailability;
    rating?: number;
    reviews?: number;
    oldPrice?: number | null;
    price?: number;
    points?: number;
    attributes?: {
        uuid?: string;
        label?: string;
        values?: {
            uuid?: string;
            value?: string;
        }[];
    }[];
    variants?: {
        values?: {
            uuid?: string;
            label?: string;
            value?: string;
        }[];
        oldPrice?: number | null;
        price?: number;
    }[];
    socialLinks?: {
        icon?: string;
        name?: string;
        url?: string;
    }[];
    images?: string[];
    details?: string;
    about?: string;
    relatedProducts: ProductCardProps[];
    isPurchasable: boolean;
    canReserve: boolean;
    loading: boolean;
}

export const ProductDetail: FC<ProductDetailProps> = ({
    uuid,
    name,
    merchant,
    category,
    availability,
    rating,
    reviews,
    oldPrice,
    price,
    points,
    attributes,
    variants,
    socialLinks,
    images,
    details,
    about,
    relatedProducts,
    isPurchasable,
    canReserve,
    loading,
}) => {
    const { isModuleActive } = useModule();
    const { useGrayscaleTheme } = useThemes();
    const { allSettings } = useData();
    const { translateAvailability, getAvailabilityTypographyColor } = useProductAvailability();
    const { t } = useTranslation();
    const [title, setTitle] = useState('');
    const [products, setProducts] = useState('');

    useEffect(() => {
        setProducts(String(getDynamicMenuItemValue(allSettings?.data, 1)));
        setTitle(String(getDynamicMenuItemValue(allSettings?.data, 3)));
    }, [allSettings]);

    const translations = {
        priceOnRequest: t('price-on-request'),
        title: t('product-detail-component.title'),
        merchantLabel: t('product-detail-component.merchant.label'),
        categoryLabel: t('product-detail-component.category.label'),
        availabilityLabel: t('product-detail-component.availability.label'),
        pointsAmount: (points: number) => t('product-detail-component.order-points.amount', { count: points }),
        popoverTitle: t('product-detail-component.order-points.popover.title'),
        select: t('select-default-option'),
        details: t('product-detail-component.details'),
        about: t('product-detail-component.about'),
        relatedProductsTitle: t('product-detail-component.related-products.title'),
        relatedProductsButton: t('product-detail-component.related-products.button'),
        vatIncluded: t('vat-included'),
    };

    const tabs: TabsProps['tabs'] = [];
    details && tabs.push({ label: translations.details, content: details });
    about && tabs.push({ label: translations.about, content: about });

    const [selectedAttributes, setSelectedAttributes] = useState<{ attributeUuid?: string; valueUuid?: string }[]>([]);

    const defaultVariant = { price, oldPrice, points };

    const variant = getVariantFromAttributes(selectedAttributes, defaultVariant, variants);
    const shouldDisplayPrice = displayPrice(variant.price);
    const formattedVariantPrice = shouldDisplayPrice ? formatPrice(variant.price) : '';

    const hasAttributesToSelect = attributes?.some((attr) => attr.values && attr.values.length > 0);

    const areAllAttributesSelected = hasAttributesToSelect
        ? attributes?.every((attr) =>
              attr.values?.length ? selectedAttributes.some((sel) => sel.attributeUuid === attr.uuid) : true
          )
        : true;

    const { userAgeAllowed, over18Modal } = useAgeRestriction({
        needsRestriction: category?.over18 === 1,
    });

    const popoverContent = allSettings?.data?.module_points_gamification?.description;

    const inCatalogMode = isModuleActive(Modules.catalogMode);
    const marketplaceCanMakeSales = !inCatalogMode || isModuleActive(Modules.paymentAndPickupAtStore);

    const canAddToCart = !inCatalogMode && isPurchasable;

    const canSelectAttributes = canAddToCart || canReserve;

    const attributesValues = Object.fromEntries(
        selectedAttributes.flatMap(({ attributeUuid, valueUuid }) => {
            const value = attributes?.find((a) => a.uuid === attributeUuid)?.values?.find((v) => v.uuid === valueUuid);

            return attributeUuid && value ? [[attributeUuid, { uuid: value.uuid!, value: value.value! }]] : [];
        })
    );

    return (
        <div className={`${useGrayscaleTheme ? styles['theme-grayscale'] : ''}`} data-testid="product-detail">
            <StickyHeader menuActivePage={MenuPages.Products} />

            <Container>
                <>
                    {!loading && (
                        <BreadcrumbsBanner
                            title={title}
                            links={[
                                { name: products, url: 'products' },
                                { name: merchant?.name || '', url: `products?stores=${merchant?.uuid}` },
                            ]}
                            currentPage={name || ''}
                        />
                    )}
                </>
            </Container>

            <Space spaceBreakpoints={{ xs: { space: 50 } }} />

            <Container>
                <>
                    {!loading && (
                        <>
                            <div className="row">
                                <div className="col-12 col-lg-6">
                                    <ProductGallery images={images || []} />
                                </div>
                                <div className="col-12 col-lg-6">
                                    <Typography
                                        classes="mb-2"
                                        tagVariant={TypographyTagVariants.h2}
                                        appearanceLevel={TypographyAppearanceVariants.level3}>
                                        {name}
                                    </Typography>

                                    {/* merchant + category + availability */}
                                    <div className="d-flex flex-wrap align-items-center gap-2 mt-4 mb-4">
                                        <div>
                                            <Typography tagVariant={TypographyTagVariants.span} color="muted">
                                                {`${translations.merchantLabel}: `}
                                            </Typography>
                                            {merchant && (
                                                <Anchor
                                                    href={`${SITE_URLS.products}?stores=${merchant.uuid}`}
                                                    mode={AnchorMode.none}
                                                    classes={styles.link}>
                                                    {merchant.name || ''}
                                                </Anchor>
                                            )}
                                        </div>
                                        <div>
                                            <Typography tagVariant={TypographyTagVariants.span} color="muted">
                                                ∙
                                            </Typography>
                                        </div>
                                        <div>
                                            <Typography tagVariant={TypographyTagVariants.span} color="muted">
                                                {`${translations.categoryLabel}: `}
                                            </Typography>
                                            {category?.name && (
                                                <Anchor
                                                    href={`${SITE_URLS.products}?categories=${category.uuid}`}
                                                    mode={AnchorMode.none}
                                                    classes={styles.link}>
                                                    {category.name}
                                                </Anchor>
                                            )}
                                        </div>
                                        {marketplaceCanMakeSales && availability !== undefined ? (
                                            <>
                                                <div>
                                                    <Typography tagVariant={TypographyTagVariants.span} color="muted">
                                                        ∙
                                                    </Typography>
                                                </div>
                                                <div>
                                                    <Typography tagVariant={TypographyTagVariants.span} color="muted">
                                                        {`${translations.availabilityLabel}: `}
                                                    </Typography>
                                                    <Typography
                                                        tagVariant={TypographyTagVariants.span}
                                                        color={getAvailabilityTypographyColor(availability)}>
                                                        {translateAvailability(availability)}
                                                    </Typography>
                                                </div>
                                            </>
                                        ) : (
                                            <></>
                                        )}
                                    </div>

                                    <Rating rating={rating} reviews={reviews} />

                                    {/* price */}
                                    <div className="mb-2 mb-md-3">
                                        {displayOldPrice(variant.oldPrice, variant.price) && (
                                            <div className="d-inline-block" data-testid="old-price">
                                                <Typography
                                                    classes={`${styles.price} mb-0 me-4`}
                                                    fontSize="36"
                                                    styles={{ lineThrough: true }}>
                                                    {formatPrice(variant.oldPrice)}
                                                </Typography>
                                            </div>
                                        )}
                                        <div className="d-inline-flex flex-row align-items-start" data-testid="price">
                                            <Typography classes={`${styles.price} mb-0`} fontWeight="800" fontSize="36">
                                                {shouldDisplayPrice ? formattedVariantPrice : translations.priceOnRequest}
                                            </Typography>
                                            {shouldDisplayPrice && (
                                                <Typography classes="mb-0 mt-3 p-2" fontSize="12" color="muted">
                                                    {translations.vatIncluded}
                                                </Typography>
                                            )}
                                        </div>
                                    </div>

                                    {/* points */}
                                    {!inCatalogMode && isModuleActive(Modules.pointsGamification) && variant.points ? (
                                        <div
                                            className={`${styles.points} d-flex align-items-center text-muted gap-2 mb-3 mb-md-4`}>
                                            <Typography tagVariant={TypographyTagVariants.span} fontSize="16">
                                                <i className="fa-light fa-trophy fa-xl" />
                                            </Typography>
                                            <Typography classes="mb-0" fontSize="16">
                                                {translations.pointsAmount(variant.points)}
                                            </Typography>
                                            {popoverContent && (
                                                <PopOver
                                                    title={translations.popoverTitle}
                                                    text={popoverContent}
                                                    placement="bottom">
                                                    <i className="fa-light fa-circle-info fa-xl"></i>
                                                </PopOver>
                                            )}
                                        </div>
                                    ) : (
                                        <></>
                                    )}

                                    {/* attributes */}
                                    {canSelectAttributes ? (
                                        <ProductAttributesSelector
                                            availableAttributes={attributes || []}
                                            selected={selectedAttributes}
                                            onChange={(valueUuid, attributeUuid) => {
                                                setSelectedAttributes((previous) => {
                                                    const otherFilters = previous.filter(
                                                        (item) => item.attributeUuid !== attributeUuid
                                                    );
                                                    return valueUuid
                                                        ? [
                                                              ...otherFilters,
                                                              { attributeUuid: `${attributeUuid}`, valueUuid: valueUuid },
                                                          ]
                                                        : otherFilters;
                                                });
                                            }}
                                        />
                                    ) : (
                                        <>
                                            {(attributes || []).map((attribute) => {
                                                return (
                                                    <div key={attribute.uuid || ''} className="d-flex gap-2 mb-3 mb-md-4">
                                                        <Typography
                                                            tagVariant={TypographyTagVariants.span}
                                                            fontWeight="800"
                                                            fontSize="16">
                                                            {`${attribute.label}: `}
                                                        </Typography>

                                                        {(attribute.values || []).map((item) => item.value || '').join(', ')}
                                                    </div>
                                                );
                                            })}
                                        </>
                                    )}

                                    <AddToCartOrReserve
                                        product={{
                                            uuid: uuid || '',
                                            attributesValuesUuids: selectedAttributes?.map((item) => item.valueUuid || '') || [],
                                            name: name || '',
                                            image: images ? images[0] : null,
                                            availability: availability,
                                        }}
                                        attributesValues={attributesValues}
                                        merchant={{
                                            uuid: merchant?.uuid || '',
                                            name: merchant?.name || '',
                                        }}
                                        canAddToCart={canAddToCart}
                                        canReserve={canReserve}
                                        disabled={!areAllAttributesSelected || !userAgeAllowed}
                                    />

                                    {/* social links */}
                                    {socialLinks && socialLinks.length > 0 && (
                                        <>
                                            <Space spaceBreakpoints={{ xs: { space: 30 } }} />
                                            <hr className="my-0" />
                                            <Space spaceBreakpoints={{ xs: { space: 30 } }} />

                                            <ul className="nav d-inline-flex justify-content-center">
                                                {socialLinks
                                                    .filter((item) => item.url)
                                                    .map((item, index) => (
                                                        <li
                                                            key={`social-link-${index}`}
                                                            className={`nav-item px-2 ${index === 0 && 'ps-0'} ${index === socialLinks.length - 1 && 'pe-0'}`}>
                                                            <Anchor
                                                                href={item.url || ''}
                                                                mode={AnchorMode.none}
                                                                classes="nav-link p-0 text-body-secondary text-decoration-none"
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                ariaLabel={item.name}>
                                                                <RoundIcon
                                                                    variant={RoundIconVariants.variant2}
                                                                    icon={item.icon}
                                                                />
                                                            </Anchor>
                                                        </li>
                                                    ))}
                                            </ul>
                                        </>
                                    )}
                                </div>
                            </div>

                            <Tabs classes="pt-4" tabs={tabs} />
                        </>
                    )}
                </>
            </Container>

            {!loading && relatedProducts.length > 0 && (
                <>
                    <Space spaceBreakpoints={{ xs: { space: 50 }, md: { space: 100 } }} />

                    <ProductsHighlights
                        title={translations.relatedProductsTitle}
                        actionLabel={translations.relatedProductsButton}
                        linkTo="products"
                        products={relatedProducts.map((product) => ({
                            ...product,
                            titleTagVariant: TypographyTagVariants.h3,
                        }))}
                    />
                </>
            )}

            <Space spaceBreakpoints={{ xs: { space: 50 }, md: { space: 100 } }} />

            <MarketplaceAdvertising />

            <Footer mode="normal" />

            {over18Modal}
        </div>
    );
};

export interface AddToCartOrReserveProps {
    product: {
        uuid: string;
        attributesValuesUuids: string[];
        name: string;
        image: string | null;
        availability?: ProductAvailability;
    };
    attributesValues?: Record<string, { uuid: string; value: string }>;
    merchant: {
        uuid: string;
        name: string;
    };
    canAddToCart: boolean;
    canReserve: boolean;
    disabled: boolean;
}

export const AddToCartOrReserve: FC<AddToCartOrReserveProps> = ({
    product,
    attributesValues,
    merchant,
    canAddToCart,
    canReserve,
    disabled,
}) => {
    const location = useLocation();
    const { navigate } = useCustomNavigate();
    const { showProductAddedToCartNotification } = useNotifications();
    const { request: requestAddProductToCart, loading, lastSuccessfulResponseTime } = useAddProductToCart();
    const { login } = useData();
    const { t } = useTranslation();

    const translations = {
        addToCart: t('product-detail-component.add-to-cart-or-reserve.add-to-cart'),
        feedbackAddedToCart: t('product-detail-component.add-to-cart-or-reserve.feedback.added-to-cart'),
        reserve: t('product-detail-component.add-to-cart-or-reserve.reserve'),
        contact: t('product-detail-component.add-to-cart-or-reserve.contact'),
    };

    const [quantity, setQuantity] = useState<number>(1);

    const [notificationShown, setNotificationShown] = useState(false);
    const [viewReserveProductModal, setViewReserveProductModal] = useState<boolean>(false);

    useEffect(() => {
        if (lastSuccessfulResponseTime !== null && !notificationShown) {
            showProductAddedToCartNotification({
                message: translations.feedbackAddedToCart,
                product: {
                    name: product.name,
                    image: product.image || '',
                    quantity: quantity,
                },
            });
            setNotificationShown(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastSuccessfulResponseTime, notificationShown]);

    const handleOnClickAddToCart = () => {
        setNotificationShown(false);
        requestAddProductToCart({
            product_uuid: product.uuid,
            quantity: quantity,
            attributes_values_uuids: product.attributesValuesUuids,
        });
    };

    const handleOnClickReserve = () => {
        if (login === null) {
            const redirectTo = encodeURIComponent(location.pathname + location.search);
            navigate(`/${SITE_URLS.login}?redirectTo=${redirectTo}`);
        } else {
            setViewReserveProductModal(true);
        }
    };

    const handleOnClickContact = () => {
        navigate(`/${SITE_URLS.merchantDetail.replace(':uuid', `${merchant.uuid}`)}`);
    };

    const amountInput = (
        <InputAmount min={1} value={quantity} onChange={(value) => setQuantity(value)} disabled={disabled || loading} />
    );

    return (
        <>
            <div className="d-flex flex-column gap-3">
                <div className="d-flex align-items-center gap-2">
                    {canAddToCart && canReserve ? (
                        <>
                            {amountInput}
                            <ButtonGroup>
                                <Button
                                    mode={ButtonMode.primary}
                                    icon="fa-regular fa-basket-shopping"
                                    label={translations.addToCart}
                                    disabled={disabled || loading}
                                    onClick={handleOnClickAddToCart}
                                />
                                <Button
                                    mode={ButtonMode.primary}
                                    label=""
                                    disabled={disabled || loading}
                                    dropdown={{
                                        side: 'right',
                                        options: [
                                            {
                                                label: translations.reserve,
                                                onClick: handleOnClickReserve,
                                            },
                                        ],
                                    }}
                                />
                            </ButtonGroup>
                        </>
                    ) : canAddToCart ? (
                        <>
                            {amountInput}
                            <Button
                                mode={ButtonMode.primary}
                                icon="fa-regular fa-basket-shopping"
                                label={translations.addToCart}
                                disabled={disabled || loading}
                                onClick={handleOnClickAddToCart}
                            />
                        </>
                    ) : canReserve ? (
                        <>
                            {amountInput}
                            <ButtonGroup>
                                <Button
                                    mode={ButtonMode.primary}
                                    label={translations.reserve}
                                    disabled={disabled || loading}
                                    onClick={handleOnClickReserve}
                                />
                                <Button
                                    mode={ButtonMode.primary}
                                    label=""
                                    dropdown={{
                                        side: 'right',
                                        options: [
                                            {
                                                label: translations.contact,
                                                onClick: handleOnClickContact,
                                            },
                                        ],
                                    }}
                                />
                            </ButtonGroup>
                        </>
                    ) : (
                        <Anchor mode={AnchorMode.primary} href={SITE_URLS.merchantDetail.replace(':uuid', `${merchant.uuid}`)}>
                            {translations.contact}
                        </Anchor>
                    )}
                </div>
            </div>
            {canReserve && (
                <ReserveProductModal
                    product={{
                        uuid: product.uuid || '',
                        name: product.name || '',
                        image: product.image || '',
                        quantity: quantity,
                        availability: product.availability,
                    }}
                    attributesValues={attributesValues}
                    merchant={merchant.name}
                    show={viewReserveProductModal}
                    onClose={() => setViewReserveProductModal(false)}
                />
            )}
        </>
    );
};
